import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { authzApi, type Permission, type Role } from '../../api/authz';

export function PermissionMatrixPage() {
  const [roles, setRoles] = useState<Role[]>([]);
  const [permissions, setPermissions] = useState<Permission[]>([]);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    void (async () => {
      setLoading(true);
      try {
        const [r, p] = await Promise.all([authzApi.listRoles(), authzApi.listPermissions()]);
        setRoles(r);
        setPermissions(p);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to load matrix');
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  function hasPermission(role: Role, key: string) {
    return (role.permissions ?? []).includes(key);
  }

  return (
    <div className="mx-auto max-w-6xl p-6">
      <Link to="/authorization" className="text-sm text-slate-600 hover:underline">← Dashboard</Link>
      <h1 className="mt-4 text-2xl font-semibold text-slate-900">Permission matrix</h1>
      <p className="mt-1 text-sm text-slate-600">Role × permission assignment overview.</p>
      {error && <p className="mt-4 rounded-lg bg-red-50 p-3 text-sm text-red-700">{error}</p>}
      {loading ? (
        <p className="mt-6 text-slate-600">Loading…</p>
      ) : (
        <div className="mt-6 overflow-x-auto rounded-xl border border-slate-200 bg-white">
          <table className="min-w-full text-sm">
            <thead>
              <tr className="border-b border-slate-200 bg-slate-50 text-left">
                <th className="p-3 font-medium text-slate-700">Role</th>
                {permissions.map((p) => (
                  <th key={p.key} className="p-3 font-medium text-slate-700 whitespace-nowrap">{p.key}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {roles.map((role) => (
                <tr key={role.id} className="border-b border-slate-100">
                  <td className="p-3 font-medium text-slate-900">{role.name}</td>
                  {permissions.map((p) => (
                    <td key={p.key} className="p-3 text-center">
                      {hasPermission(role, p.key) ? (
                        <span className="inline-block h-2 w-2 rounded-full bg-emerald-500" title="Granted" />
                      ) : (
                        <span className="inline-block h-2 w-2 rounded-full bg-slate-200" title="Not granted" />
                      )}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
